import React, { useState } from "react";
import { BarChart3, CalendarClock, CheckCircle2, LineChart, Loader2 } from "lucide-react";
import ActiveCardShell from "../ActiveCardShell";
import { sleep, RunButton, useAlive } from "../phase2/shared";

const METRICS = [
  { key: "volume", label: "Volume", color: "#22d3ee", data: [42, 55, 51, 68, 74, 88] },
  { key: "residuals", label: "Residuals", color: "#10b981", data: [20, 26, 31, 33, 40, 47] },
  { key: "approvals", label: "Approvals", color: "#a3e635", data: [12, 18, 15, 24, 28, 34] },
  { key: "chargebacks", label: "Chargebacks", color: "#f59e0b", data: [9, 8, 7, 6, 4, 3] },
];
const MONTHS = ["May", "Jun", "Jul", "Aug", "Sep", "Oct"];

// Toggle metrics + chart type; the chart renders live, then the report gets scheduled.
export default function CustomReportCard() {
  const [on, setOn] = useState([]);
  const [type, setType] = useState("bar");
  const [scheduled, setScheduled] = useState(false);
  const [busy, setBusy] = useState(false);
  const alive = useAlive();

  const toggle = (k) => { setScheduled(false); setOn((o) => (o.includes(k) ? o.filter((x) => x !== k) : [...o, k])); };
  const run = async () => {
    setBusy(true); setOn([]); setScheduled(false); setType("bar");
    for (const k of ["volume", "residuals", "approvals"]) { await sleep(550); if (!alive.current) return; setOn((o) => [...o, k]); }
    await sleep(700); if (!alive.current) return; setType("line");
    await sleep(900); if (!alive.current) return; setScheduled(true);
    setBusy(false);
  };
  const shown = METRICS.filter((m) => on.includes(m.key));

  return (
    <ActiveCardShell id="T7400cr" kicker="CUSTOM BUILD" title="Custom Report Builder" Icon={BarChart3}
      actions={<RunButton tid="veroogle-t7400-run" running={busy} onClick={run} Icon={busy ? Loader2 : BarChart3} label="Build report" />}>
      <div className="flex flex-wrap items-center gap-2 mb-4">
        {METRICS.map((m) => (
          <button key={m.key} type="button" disabled={busy} onClick={() => toggle(m.key)} data-testid={`veroogle-t7400-metric-${m.key}`}
            className={`rounded-full border px-3 py-1.5 text-xs transition-colors ${on.includes(m.key) ? "text-white" : "border-white/10 text-slate-400 hover:text-white"}`}
            style={on.includes(m.key) ? { borderColor: `${m.color}99`, background: `${m.color}1f` } : undefined}>{m.label}</button>
        ))}
        <div className="ml-auto flex rounded-lg border border-white/10 overflow-hidden">
          {[["bar", BarChart3], ["line", LineChart]].map(([t, I]) => (
            <button key={t} type="button" disabled={busy} onClick={() => setType(t)} data-testid={`veroogle-t7400-type-${t}`} aria-label={`${t} chart`}
              className={`p-1.5 transition-colors ${type === t ? "bg-brand-cyan/20 text-brand-cyan" : "text-slate-500 hover:text-white"}`}><I className="w-4 h-4" /></button>
          ))}
        </div>
      </div>
      <Chart shown={shown} type={type} />
      {scheduled && (
        <div data-testid="veroogle-t7400-outcome" className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-500 text-navy-950 font-mono text-xs sm:text-sm font-bold tracking-[0.14em] uppercase shadow-[0_0_24px_rgba(16,185,129,0.4)] animate-in zoom-in-75 fade-in">
          <CalendarClock className="w-4 h-4" /> Report scheduled · Mondays 8am
        </div>
      )}
      {!scheduled && !busy && shown.length > 0 && (
        <button type="button" onClick={() => setScheduled(true)} data-testid="veroogle-t7400-schedule"
          className="mt-4 inline-flex items-center gap-1.5 rounded-lg border border-brand-cyan/40 px-3 py-1.5 font-mono text-[11px] tracking-[0.14em] uppercase text-brand-cyan hover:bg-brand-cyan/10 transition-colors">
          <CheckCircle2 className="w-3.5 h-3.5" /> Schedule weekly
        </button>
      )}
    </ActiveCardShell>
  );
}

function Chart({ shown, type }) {
  const W = 300, H = 110, n = MONTHS.length, gw = W / n;
  return (
    <div className="rounded-xl border border-white/10 bg-navy-950/50 p-3" data-testid="veroogle-t7400-chart">
      {shown.length === 0 ? (
        <div className="h-[130px] flex items-center justify-center text-xs text-slate-500">Pick metrics to build your chart…</div>
      ) : (
        <svg viewBox={`0 0 ${W} ${H + 18}`} className="w-full h-[130px]">
          {MONTHS.map((m, i) => <text key={m} x={i * gw + gw / 2} y={H + 14} textAnchor="middle" className="fill-slate-500" fontSize="8">{m}</text>)}
          {type === "bar" && shown.map((s, si) => s.data.map((v, i) => {
            const bw = (gw - 10) / shown.length, h = (v / 100) * H;
            return <rect key={`${s.key}-${i}`} x={i * gw + 5 + si * bw} y={H - h} width={bw - 2} height={h} rx="2" fill={s.color} opacity="0.85"
              style={{ transformOrigin: `0 ${H}px`, animation: "nexGrow .6s ease-out" }} />;
          }))}
          {type === "line" && shown.map((s) => (
            <polyline key={s.key} fill="none" stroke={s.color} strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round"
              points={s.data.map((v, i) => `${i * gw + gw / 2},${H - (v / 100) * H}`).join(" ")}
              style={{ strokeDasharray: 600, animation: "nexDraw 1s ease-out" }} />
          ))}
          <style>{"@keyframes nexGrow{from{transform:scaleY(0)}to{transform:scaleY(1)}}@keyframes nexDraw{from{stroke-dashoffset:600}to{stroke-dashoffset:0}}"}</style>
        </svg>
      )}
    </div>
  );
}
